import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Map, MapPin, Clock, User, ExternalLink, Loader2, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";

const todayIso = () => new Date().toISOString().slice(0, 10);

export const AdminRoutes = () => {
  const [date, setDate] = useState(todayIso());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (d) => {
    setLoading(true);
    try {
      setData(await adminApi.routes(d));
    } catch (e) {
      toast.error("Could not load routes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(date); /* eslint-disable-next-line */ }, [date]);

  return (
    <div data-testid="admin-routes-page">
      <h1 className="font-display font-black uppercase text-3xl sm:text-4xl text-[#111619]">Routes</h1>
      <p className="text-[#3f4b52] mt-1">
        Daily run-sheet grouped by fitter. Tap "Open in Maps" to dispatch the team.
      </p>

      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <label className="text-xs font-display uppercase tracking-widest text-[#3f4b52] inline-flex items-center gap-2">
          <CalendarDays size={14} /> Date
        </label>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          data-testid="routes-date-input"
          className="h-10 px-3 bg-white border border-[#cfd7db] rounded-md text-sm"
        />
        <div className="text-xs text-[#3f4b52] ml-auto">
          {data?.total_stops ?? 0} stop{(data?.total_stops ?? 0) === 1 ? "" : "s"}
        </div>
      </div>

      {loading ? (
        <div className="mt-10 text-[#3f4b52] inline-flex items-center gap-2">
          <Loader2 className="animate-spin" size={16} /> Loading…
        </div>
      ) : !data || data.groups.length === 0 ? (
        <div className="mt-10 bg-white border border-dashed border-[#cfd7db] rounded-md p-10 text-center">
          <Map size={36} className="text-[#9aa6ad] mx-auto" />
          <div className="mt-3 font-display uppercase tracking-widest text-sm text-[#3f4b52]">
            No bookings scheduled for this day
          </div>
          <Link to="/admin/bookings" className="text-xs text-[#f28a16] hover:underline mt-2 inline-block">
            Open bookings →
          </Link>
        </div>
      ) : (
        <div className="mt-6 space-y-5">
          {data.groups.map((g, idx) => (
            <div key={g.fitter} className="bg-white border border-[#cfd7db] rounded-md overflow-hidden" data-testid={`route-group-${idx}`}>
              <div className="px-4 py-3 bg-[#20282c] text-white flex items-center justify-between flex-wrap gap-2">
                <div className="font-display uppercase tracking-widest text-sm inline-flex items-center gap-2">
                  <User size={16} className="text-[#ffc438]" />
                  {g.fitter}
                  <span className="text-[#9aa6ad] text-xs">· {g.stops.length} stop{g.stops.length === 1 ? "" : "s"}</span>
                </div>
                {g.gmaps_url && (
                  <a
                    href={g.gmaps_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-[#f28a16] text-white px-3 h-8 rounded-md font-display uppercase tracking-widest text-[11px] inline-flex items-center gap-1.5 hover:brightness-110"
                    data-testid={`route-maps-${idx}`}
                  >
                    <ExternalLink size={12} /> Open in Maps
                  </a>
                )}
              </div>
              <ol className="divide-y divide-[#eef2f4]">
                {g.stops.map((s, i) => (
                  <li key={s.id} className="px-4 py-3 flex items-start gap-3 text-sm">
                    <span className="shrink-0 h-7 w-7 rounded-full bg-[#f28a16]/15 text-[#f28a16] inline-flex items-center justify-center font-display font-black text-xs">
                      {i + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="font-display font-bold text-[#111619] truncate">
                        {s.client_name} <span className="text-[#3f4b52] font-normal">· {s.registration}</span>
                      </div>
                      <div className="text-xs text-[#3f4b52] truncate flex items-center gap-1.5 mt-0.5">
                        <Clock size={12} className="shrink-0" /> {s.fitment_time || "Any time"}
                        {s.location && (
                          <>
                            <span>·</span>
                            <MapPin size={12} className="shrink-0" />
                            <span className="truncate">{s.location}</span>
                          </>
                        )}
                      </div>
                      {s.vehicle_make_model && (
                        <div className="text-xs text-[#3f4b52] mt-0.5">{s.vehicle_make_model}</div>
                      )}
                    </div>
                    {s.location && (
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(s.location)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-[#f28a16] hover:underline inline-flex items-center gap-1 shrink-0"
                      >
                        <MapPin size={12} /> Map
                      </a>
                    )}
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminRoutes;
